import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import timeZones from '../../../utils/timeZone.json';
import Loader from '../../../components/common/Loader';
import NoData from '../../../components/common/NoData';
import AdminPage from '../../../components/admin/AdminPage';
import {
    getAnnouncementsRole,
    getAnnouncements,
    createAnnouncements,
    updateAnnouncements,
    deleteAnnouncement,
    sendAnnouncements,
} from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';

const DefaultTimeZone = process.env.REACT_APP_DEFAULT_TIMEZONE;

function Announcement() {
    const { permissions } = usePermissions();
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [announcementData, setAnnouncementData] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [timeZoneSearch, setTimeZoneSearch] = useState('');
    const [isTimeZoneDropdownOpen, setIsTimeZoneDropdownOpen] = useState(false);
    const timeZoneTriggerRef = useRef(null);
    const [timeZoneDropdownRect, setTimeZoneDropdownRect] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        recordsPerPage: 10
    });
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        scheduleDate: '',
        role: '',
        timeZone: DefaultTimeZone,
        sendNotification: true,
        sendEmail: false
    });
    const [formErrors, setFormErrors] = useState({
        name: '',
        description: '',
        scheduleDate: '',
        role: '',
        timeZone: ''
    });

    // Constants for validation
    const MAX_NAME_LENGTH = 2000;
    const MAX_DESCRIPTION_LENGTH = 2000;
    const MIN_NAME_LENGTH = 5;
    const MIN_DESCRIPTION_LENGTH = 20;

    const user_id = JSON.parse(localStorage.getItem('user')).id;

    // Validation function
    const validateForm = () => {
        let isValid = true;
        const errors = {
            name: '',
            description: '',
            scheduleDate: '',
            role: '',
            timeZone: ''
        };

        // Name validation
        if (!formData.name.trim()) {
            errors.name = 'Announcement name is required';
            isValid = false;
        } else if (formData.name.trim().length < MIN_NAME_LENGTH) {
            errors.name = `Name must be at least ${MIN_NAME_LENGTH} characters`;
            isValid = false;
        } else if (formData.name.trim().length > MAX_NAME_LENGTH) {
            errors.name = `Name cannot exceed ${MAX_NAME_LENGTH} characters`;
            isValid = false;
        }

        // Description validation
        if (!formData.description.trim()) {
            errors.description = 'Description is required';
            isValid = false;
        } else if (formData.description.trim().length < MIN_DESCRIPTION_LENGTH) {
            errors.description = `Description must be at least ${MIN_DESCRIPTION_LENGTH} characters`;
            isValid = false;
        } else if (formData.description.trim().length > MAX_DESCRIPTION_LENGTH) {
            errors.description = `Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`;
            isValid = false;
        }

        // Schedule Date validation
        if (!formData.scheduleDate) {
            errors.scheduleDate = 'Schedule date is required';
            isValid = false;
        } else {
            const selectedDate = moment.tz(formData.scheduleDate, formData.timeZone);
            const now = moment();
            if (selectedDate.isBefore(now)) {
                errors.scheduleDate = 'Schedule date cannot be in the past';
                isValid = false;
            }
        }

        // Role validation
        if (!formData.role) {
            errors.role = 'Please select a role';
            isValid = false;
        }

        // Time Zone validation
        if (!formData.timeZone) {
            errors.timeZone = 'Please select a time zone';
            isValid = false;
        } else if (!timeZones.some(zone => zone.value === formData.timeZone)) {
            errors.timeZone = 'Please select a valid time zone';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    // Fetch announcements data
    const fetchAnnouncements = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAnnouncements({
                page: pagination.currentPage,
                limit: pagination.recordsPerPage,
                search: search,
                status: statusFilter
            });

            // console.log('Announcement response', response);

            if (response.success) {
                setAnnouncementData(response.data || []);
                if (response.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        totalPages: response.pagination.totalPages || 1,
                        totalRecords: response.pagination.totalRecords || 0
                    }));
                }
            } else {
                toast.error('Failed to fetch announcements');
            }
        } catch (error) {
            console.error('Error fetching announcements:', error);
            toast.error('Error fetching announcements');
        } finally {
            setLoading(false);
        }
    }, [pagination.currentPage, pagination.recordsPerPage, search, statusFilter]);

    // Fetch roles for dropdown
    const fetchRoles = useCallback(async () => {
        try {
            const response = await getAnnouncementsRole();
            // console.log('Announcement Roles response', response);

            if (response.success) {
                setRoles(response.data || []);
            } else {
                toast.error('Failed to fetch roles');
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Error fetching roles');
        }
    }, []);

    // Load data on component mount and when dependencies change
    useEffect(() => {
        fetchAnnouncements();
        fetchRoles();
    }, [fetchAnnouncements, fetchRoles]);

    const getStatusClass = (status) => {
        const statusClasses = {
            'Published': 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 border border-green-200 text-green-700 p-2',
            'Pending': 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 border border-blue-200 text-blue-700 p-2'
        }
        return statusClasses[status] || 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700 p-2'
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [name]: ''
        }));

        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else if (name === 'name' && value.length <= MAX_NAME_LENGTH) {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        } else if (name === 'description' && value.length <= MAX_DESCRIPTION_LENGTH) {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        } else if (name !== 'name' && name !== 'description') {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editMode && !permissions.announcement_management_edit) {
            toast.warning("You don't have permission to edit announcements");
            return;
        }

        if (!editMode && !permissions.announcement_management_create) {
            toast.warning("You don't have permission to create announcements");
            return;
        }

        // Validate form before submission
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            // Convert the local date to UTC based on selected timezone
            const localDate = moment.tz(formData.scheduleDate, formData.timeZone);
            const utcDate = localDate.utc().format();

            const payload = {
                createdBy: user_id,
                title: formData.name.trim(),
                message: formData.description.trim(),
                scheduleDateTime: utcDate,
                roles: formData.role,
                send_notification: 1, // Always set to 1
                send_email: 0, // Always set to 0
                time_zone: formData.timeZone // Add timezone to payload
            };

            // console.log('Submitting announcement:', payload);

            let response;
            if (editMode && selectedAnnouncement) {
                response = await updateAnnouncements(selectedAnnouncement.id, payload);
                if (response.success) {
                    toast.success('Announcement updated successfully');
                    fetchAnnouncements();
                    handleCloseModal();
                } else {
                    toast.error(response.message || 'Failed to update announcement');
                }
            } else {
                response = await createAnnouncements(payload);
                if (response.success) {
                    toast.success('Announcement created successfully');
                    fetchAnnouncements();
                    handleCloseModal();
                } else {
                    toast.error(response.message || 'Failed to create announcement');
                }
            }
        } catch (error) {
            console.error('Error saving announcement:', error);
            toast.error('Error saving announcement');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditMode(false);
        setSelectedAnnouncement(null);
        setIsTimeZoneDropdownOpen(false);
        setTimeZoneDropdownRect(null);
        setFormData({
            name: '',
            description: '',
            scheduleDate: '',
            role: '',
            timeZone: DefaultTimeZone,
            sendNotification: false,
            sendEmail: false
        });
        setFormErrors({
            name: '',
            description: '',
            scheduleDate: '',
            role: '',
            timeZone: ''
        });
    };

    const handleEdit = (announcement) => {
        if (!permissions.announcement_management_edit) {
            toast.warning("You don't have permission to edit announcements");
            return;
        }



        setSelectedAnnouncement(announcement);
        setEditMode(true);
        setFormData({
            name: announcement.title || '',
            description: announcement.message || '',
            scheduleDate: moment.tz(announcement.scheduleDateTime, 'UTC').tz(announcement.time_zone || DefaultTimeZone).format('YYYY-MM-DDTHH:mm'), // ✅ Convert from UTC to local time zone
            role: announcement.roles || '',
            timeZone: announcement.time_zone || DefaultTimeZone,
            sendNotification: announcement.send_notification === 1,
            sendEmail: announcement.send_email === 1
        });
        setShowModal(true);
    };

    const handleDelete = async () => {
        if (!selectedAnnouncement) return;

        if (!permissions.announcement_management_delete) {
            toast.warning("You don't have permission to delete announcements");
            return;
        }

        try {
            setLoading(true);
            const response = await deleteAnnouncement(selectedAnnouncement.id);

            if (response.success) {
                toast.success('Announcement deleted successfully');
                fetchAnnouncements();
                setShowDeleteModal(false);
                setSelectedAnnouncement(null);
            } else {
                toast.error(response.message || 'Failed to delete announcement');
            }
        } catch (error) {
            console.error('Error deleting announcement:', error);
            toast.error('Error deleting announcement');
        } finally {
            setLoading(false);
        }
    };

    const handleView = (announcement) => {
        setSelectedAnnouncement(announcement);
        setShowViewModal(true);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, currentPage: newPage }));
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setPagination(prev => ({
            ...prev,
            recordsPerPage: newLimit,
            currentPage: 1
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';

        // Create a date object from the UTC string
        const date = new Date(dateString);

        // Format the date using the user's local timezone
        const formattedDate = date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: formData.timeZone || DefaultTimeZone,
            timeZoneName: 'short' // Add timezone abbreviation
        });

        return formattedDate;
    };

    return (
        <AdminPage
            title="Announcements"
            description="Create and manage announcements. Schedule when to publish and choose which roles see them."
            actions={
                <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm shadow-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                        if (!permissions.announcement_management_create) {
                            toast.warning("You don't have permission to create announcements");
                            return;
                        }
                        setShowModal(true);
                    }}
                    disabled={loading || !permissions.announcement_management_create}
                    title={permissions.announcement_management_create ? "Create Announcement" : "You don't have permission to create announcements"}
                >
                    <Icon icon="fluent:megaphone-loud-24-regular" width={18} height={18} />
                    <span>Create Announcement</span>
                </button>
            }
        >
        <>
            {/* Toolbar: search + filter */}
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden mb-4">
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="search"
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            placeholder="Search announcements..."
                            value={search}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <select
                        className="px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-[160px]"
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                    >
                        <option value="">All Status</option>
                        <option value="1">Published</option>
                        <option value="0">Pending</option>
                    </select>
                </div>
            </div>

            {/* Create / Edit Announcement Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl min-h-[min(680px,88vh)] max-h-[94vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {editMode ? 'Edit Announcement' : 'Create Announcement'}
                            </h2>
                            <button
                                type="button"
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                onClick={handleCloseModal}
                                aria-label="Close"
                            >
                                <Icon icon="mdi:close" width={22} height={22} />
                            </button>
                        </div>

                        <div className="p-5 overflow-y-auto flex-1">
                            {(editMode && !permissions.announcement_management_edit) || (!editMode && !permissions.announcement_management_create) ? (
                                <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
                                    <Icon icon="mdi:warning" width={20} height={20} className="shrink-0" />
                                    <span>{editMode ? "You don't have permission to edit announcements" : "You don't have permission to create announcements"}</span>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Announcement title <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${formErrors.name ? 'border-red-500' : 'border-gray-200'}`}
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="e.g. New course launch"
                                        />
                                        <div className="flex justify-between items-center mt-1.5">
                                            <span className={`text-xs ${formData.name.length >= MAX_NAME_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
                                                {formData.name.length} / {MAX_NAME_LENGTH}
                                            </span>
                                            {formErrors.name && <span className="text-xs text-red-500">{formErrors.name}</span>}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Message <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y min-h-[100px] ${formErrors.description ? 'border-red-500' : 'border-gray-200'}`}
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="Write the announcement message (min 20 characters)"
                                            rows={4}
                                        />
                                        <div className="flex justify-between items-center mt-1.5">
                                            <span className={`text-xs ${formData.description.length >= MAX_DESCRIPTION_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
                                                {formData.description.length} / {MAX_DESCRIPTION_LENGTH}
                                            </span>
                                            {formErrors.description && <span className="text-xs text-red-500">{formErrors.description}</span>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Schedule date & time <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="datetime-local"
                                                className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${formErrors.scheduleDate ? 'border-red-500' : 'border-gray-200'}`}
                                                name="scheduleDate"
                                                value={formData.scheduleDate}
                                                onChange={handleInputChange}
                                                disabled={editMode && selectedAnnouncement && new Date(selectedAnnouncement.scheduleDateTime) < new Date()}
                                            />
                                            {formErrors.scheduleDate && <p className="text-xs text-red-500 mt-1">{formErrors.scheduleDate}</p>}
                                            <p className="text-xs text-gray-500 mt-1">Schedule cannot be changed after publish time has passed.</p>
                                        </div>

                                        <div className="relative" ref={timeZoneTriggerRef}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Time zone <span className="text-red-500">*</span>
                                            </label>
                                            <button
                                                type="button"
                                                className={`w-full px-3 py-2.5 border rounded-xl text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${formErrors.timeZone ? 'border-red-500' : 'border-gray-200'} bg-white`}
                                                onClick={() => {
                                                    const next = !isTimeZoneDropdownOpen;
                                                    setIsTimeZoneDropdownOpen(next);
                                                    if (next && timeZoneTriggerRef.current) {
                                                        const rect = timeZoneTriggerRef.current.getBoundingClientRect();
                                                        setTimeZoneDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
                                                    } else {
                                                        setTimeZoneDropdownRect(null);
                                                    }
                                                }}
                                            >
                                                <span className="truncate">{formData.timeZone ? (timeZones.find(z => z.value === formData.timeZone)?.label || formData.timeZone) : 'Select time zone'}</span>
                                                <Icon icon={isTimeZoneDropdownOpen ? 'eva:arrow-up-fill' : 'eva:arrow-down-fill'} className="shrink-0 ml-2 w-4 h-4 text-gray-500" />
                                            </button>
                                            {isTimeZoneDropdownOpen && timeZoneTriggerRef.current && (
                                                <>
                                                    <div className="fixed inset-0 z-[60]" onClick={() => { setIsTimeZoneDropdownOpen(false); setTimeZoneDropdownRect(null); }} aria-hidden />
                                                    {timeZoneDropdownRect && createPortal(
                                                        <div
                                                            className="fixed z-[70] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[320px] min-w-[200px]"
                                                            style={{ top: timeZoneDropdownRect.top, left: timeZoneDropdownRect.left, width: timeZoneDropdownRect.width }}
                                                        >
                                                            <div className="p-2 border-b border-gray-100 bg-gray-50/50 shrink-0">
                                                                <div className="relative">
                                                                    <Icon icon="eva:search-outline" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                                    <input
                                                                        type="text"
                                                                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                                                                        placeholder="Search time zone..."
                                                                        value={timeZoneSearch}
                                                                        onChange={(e) => setTimeZoneSearch(e.target.value)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="overflow-y-auto flex-1 py-1 min-h-0">
                                                                {timeZones
                                                                    .filter(zone =>
                                                                        zone.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) ||
                                                                        zone.value.toLowerCase().includes(timeZoneSearch.toLowerCase())
                                                                    )
                                                                    .map((zone) => (
                                                                        <button
                                                                            key={zone.value}
                                                                            type="button"
                                                                            className={`w-full text-left px-3 py-2.5 text-sm rounded-lg mx-1 transition-colors ${formData.timeZone === zone.value ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                                                                            onClick={() => {
                                                                                setFormData(prev => ({ ...prev, timeZone: zone.value }));
                                                                                setIsTimeZoneDropdownOpen(false);
                                                                                setTimeZoneSearch('');
                                                                                setTimeZoneDropdownRect(null);
                                                                            }}
                                                                        >
                                                                            {zone.label}
                                                                        </button>
                                                                    ))}
                                                                {timeZones.filter(z => z.label.toLowerCase().includes(timeZoneSearch.toLowerCase()) || z.value.toLowerCase().includes(timeZoneSearch.toLowerCase())).length === 0 && (
                                                                    <p className="px-3 py-4 text-sm text-gray-500 text-center">No time zone found</p>
                                                                )}
                                                            </div>
                                                        </div>,
                                                        document.body
                                                    )}
                                                </>
                                            )}
                                            {formErrors.timeZone && <p className="text-xs text-red-500 mt-1">{formErrors.timeZone}</p>}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Target role <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${formErrors.role ? 'border-red-500' : 'border-gray-200'} ${editMode ? 'bg-gray-50 cursor-not-allowed text-gray-600' : 'bg-white'}`}
                                            name="role"
                                            value={formData.role}
                                            onChange={handleInputChange}
                                            disabled={editMode}
                                        >
                                            <option value="" disabled>Select role</option>
                                            {roles.map((role) => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                        {formErrors.role && <p className="text-xs text-red-500 mt-1">{formErrors.role}</p>}
                                        {editMode && <p className="text-xs text-gray-500 mt-1">Role cannot be changed after creation.</p>}
                                    </div>

                                    <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                                        <button
                                            type="button"
                                            className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
                                            onClick={handleCloseModal}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-primary hover:bg-primary/90 shadow-sm transition-colors disabled:opacity-50"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Saving...
                                                </>
                                            ) : editMode ? 'Update Announcement' : 'Create Announcement'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Announcements table */}
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" style={{ minWidth: '760px' }}>
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Publish date & time</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Status</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-12 text-center">
                                        <Loader />
                                    </td>
                                </tr>
                            ) : announcementData.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-12 text-center">
                                        <NoData />
                                    </td>
                                </tr>
                            ) : (
                                announcementData.map((announcement, index) => (
                                    <tr key={announcement.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3 text-gray-500 font-medium">
                                            {(pagination.currentPage - 1) * pagination.recordsPerPage + index + 1}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-900">{announcement.title}</td>
                                        <td className="px-4 py-3 text-gray-600 max-w-xs">
                                            <span className="line-clamp-2">{announcement.message}</span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {new Date(announcement.scheduleDateTime).toLocaleString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                            })}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={getStatusClass(announcement.status === 1 ? 'Published' : 'Pending')}>
                                                {announcement.status === 1 ? 'Published' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    type="button"
                                                    className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                                    title="View announcement"
                                                    onClick={() => handleView(announcement)}
                                                    disabled={loading}
                                                >
                                                    <Icon icon="fluent:eye-24-regular" width={18} height={18} />
                                                </button>
                                                {announcement.status !== 1 && (
                                                    <button
                                                        type="button"
                                                        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                                        title={permissions.announcement_management_edit ? 'Edit' : "You don't have permission to edit"}
                                                        onClick={() => handleEdit(announcement)}
                                                        disabled={loading || !permissions.announcement_management_edit}
                                                    >
                                                        <Icon icon="fluent:edit-24-regular" width={18} height={18} />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                                                    title={permissions.announcement_management_delete ? 'Delete' : "You don't have permission to delete"}
                                                    onClick={() => {
                                                        if (!permissions.announcement_management_delete) {
                                                            toast.warning("You don't have permission to delete announcements");
                                                            return;
                                                        }
                                                        setSelectedAnnouncement(announcement);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    disabled={loading || !permissions.announcement_management_delete}
                                                >
                                                    <Icon icon="fluent:delete-24-regular" width={18} height={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Rows per page</span>
                        <select
                            className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                            value={pagination.recordsPerPage}
                            onChange={handleLimitChange}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-sm text-gray-500">
                            {announcementData.length === 0 ? 0 : (pagination.currentPage - 1) * pagination.recordsPerPage + 1}–{Math.min(pagination.currentPage * pagination.recordsPerPage, (pagination.currentPage - 1) * pagination.recordsPerPage + announcementData.length)} of {pagination.totalRecords || announcementData.length}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-white hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1 || loading}
                            aria-label="Previous page"
                        >
                            <Icon icon="fluent:chevron-left-24-regular" width={18} height={18} />
                        </button>
                        <span className="text-sm text-gray-600 min-w-[100px] text-center">
                            Page {pagination.currentPage} of {pagination.totalPages || 1}
                        </span>
                        <button
                            type="button"
                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-white hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage >= (pagination.totalPages || 1) || loading}
                            aria-label="Next page"
                        >
                            <Icon icon="fluent:chevron-right-24-regular" width={18} height={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedAnnouncement && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && (setShowDeleteModal(false), setSelectedAnnouncement(null))}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                            <button type="button" className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" onClick={() => { setShowDeleteModal(false); setSelectedAnnouncement(null); }} aria-label="Close">
                                <Icon icon="mdi:close" width={22} height={22} />
                            </button>
                        </div>
                        <div className="p-5">
                            {!permissions.announcement_management_delete ? (
                                <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
                                    <Icon icon="mdi:warning" width={20} height={20} className="shrink-0" />
                                    <span>You don't have permission to delete announcements.</span>
                                </div>
                            ) : (
                                <>
                                    <p className="text-gray-700">Are you sure you want to delete &quot;{selectedAnnouncement.title}&quot;? This action cannot be undone.</p>
                                    <div className="flex justify-end gap-3 mt-5">
                                        <button type="button" className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200" onClick={() => { setShowDeleteModal(false); setSelectedAnnouncement(null); }} disabled={loading}>Cancel</button>
                                        <button type="button" className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-red-500 hover:bg-red-600" onClick={handleDelete} disabled={loading}>{loading ? 'Deleting...' : 'Delete'}</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* View Announcement Modal */}
            {showViewModal && selectedAnnouncement && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && (setShowViewModal(false), setSelectedAnnouncement(null))}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
                            <h3 className="text-lg font-semibold text-gray-900">Announcement Details</h3>
                            <button type="button" className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" onClick={() => { setShowViewModal(false); setSelectedAnnouncement(null); }} aria-label="Close">
                                <Icon icon="mdi:close" width={22} height={22} />
                            </button>
                        </div>
                        <div className="p-5 overflow-y-auto space-y-4">
                            <div>
                                <span className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Title</span>
                                <p className="text-gray-900 font-medium">{selectedAnnouncement.title}</p>
                            </div>
                            <div>
                                <span className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Message</span>
                                <p className="text-gray-700 whitespace-pre-wrap">{selectedAnnouncement.message}</p>
                            </div>
                            <div>
                                <span className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Schedule date & time</span>
                                <p className="text-gray-700">{formatDate(selectedAnnouncement.scheduleDateTime)}</p>
                            </div>
                            <div>
                                <span className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</span>
                                <span className={getStatusClass(selectedAnnouncement.status === 1 ? 'Published' : 'Pending')}>
                                    {selectedAnnouncement.status === 1 ? 'Published' : 'Pending'}
                                </span>
                            </div>
                        </div>
                        <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
                            <button type="button" className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200" onClick={() => { setShowViewModal(false); setSelectedAnnouncement(null); }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </>
        </AdminPage>
    )
}

export default Announcement
